import { isDefined } from '@duke-hq/lib';
import {
  Avatar,
  Box,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Table,
  Badge,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { FiEdit2, FiCopy } from 'react-icons/fi/index.js';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import startCase from 'lodash-es/startCase.js';
import { BadgeCheck, Landmark, Trash2, SquarePen, Ellipsis, Replace } from 'lucide-react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import {
  CourseStatus,
  CourseStatusSchema,
} from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { CourseKind } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { djs } from '@duke-hq/djs';
import { trpcAdmin, type RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  CourseCoverageBadge,
  type ResourceCoverageLike,
} from '../components/translations/TranslationCoverageBadge';
import { CourseAssignmentModal } from './CourseAssignmentModal';
import { SortOrder } from '@duke-hq/lib/pagination';
import { isCourseStatusAssignable } from '@duke-hq/shared-constants/course';
import { getCourseKindLabel } from '../lib/courseUtils';

interface SortPropsInterface {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  kind?: CourseKind | undefined;
  status?: CourseStatus | undefined;
}

const courseStatusColorSchemes: Record<CourseStatus, string> = {
  [CourseStatusSchema.enum.DRAFT]: 'gray',
  [CourseStatusSchema.enum.CLOSED]: 'orange',
  [CourseStatusSchema.enum.INACTIVE]: 'yellow',
  [CourseStatusSchema.enum.PUBLISHED]: 'purple',
  [CourseStatusSchema.enum.SUPERSEDED]: 'red',
  [CourseStatusSchema.enum.PENDING_SUPERSESSION]: 'yellow',
};

export function CoursesTable({
  courses,
  urlParams,
  handleCourseDelete,
  onFilters,
  onDuplicateCourseClick,
  onMarkSupersededCourseClick,
}: {
  courses: NonNullable<RouterOutputs['courses']['listCoursesWithPagination']>['items'];
  urlParams: SortPropsInterface;
  handleCourseDelete: (courseId: string) => void;
  onFilters: (filters: SortPropsInterface) => void;
  onDuplicateCourseClick: (courseId: string) => void;
  onMarkSupersededCourseClick: (courseId: string) => void;
}) {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedCourseForAssignment, setSelectedCourseForAssignment] = useState<{
    id: string;
    title: string;
    status: string;
  } | null>(null);

  // One batch query for the visible page's translation coverage, so each row can
  // show a course-level badge without a query per row. Course-scoped: each course
  // is measured against its own languages.
  const courseIds = useMemo(() => courses.map((course) => course.id), [courses]);
  const { data: coverage } = trpcAdmin.contentTranslations.listCoverageByCourses.useQuery(
    { courseIds },
    { enabled: courseIds.length > 0 },
  );
  const coverageByCourseId = useMemo(
    () =>
      new Map<string, { languages: readonly string[]; resources: ResourceCoverageLike[] }>(
        (coverage?.courses ?? []).map((course) => [
          course.courseId,
          { languages: course.supportedLanguages, resources: course.resources },
        ]),
      ),
    [coverage],
  );

  const { orderBy, sort } = urlParams;
  const navigate = useNavigate();
  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        orderBy: name,
        sort: newSortDirection,
      } as SortPropsInterface;

      onFilters({ ...updatedFilters });
    },
    [onFilters, sort],
  );

  const handleEditCourse = useCallback(
    (id: string) => {
      navigate(`/courses/${id}`);
    },
    [navigate],
  );

  return (
    <>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <SortableHeader
                label={t`Title`}
                columnName="title"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Kind`}
                columnName="kind"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Accredited`}
                columnName="accredited"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Units`}
                columnName="units"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Author`}
                columnName="author"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Status`}
                columnName="status"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Trans>Translations</Trans>
            </Th>
            <Th>
              <SortableHeader
                label={t`Created at`}
                columnName="created_at"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th position="sticky" right={0} zIndex={2} bg="white !important" px={8}>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <SquarePen width={20} height={20} />
              </Box>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {Array.isArray(courses) &&
            courses.length > 0 &&
            courses.map((course) => {
              const userFullName = [course?.createdBy?.first_name, course?.createdBy?.last_name]
                .filter(isDefined)
                .join(' ');

              const isAssignable = isCourseStatusAssignable(course.status);

              return (
                <Tr
                  key={course.id}
                  _hover={{ bg: 'bg.subtle', cursor: 'pointer' }}
                  onClick={() => handleEditCourse(course.id)}
                >
                  <Td>
                    <Text fontWeight="medium">{course.title}</Text>
                  </Td>

                  <Td>
                    <Text fontWeight="medium">
                      {course?.kind ? getCourseKindLabel(course.kind) : ''}
                    </Text>
                  </Td>

                  <Td>
                    {course?.accredited ? (
                      <Icon
                        as={BadgeCheck}
                        color="teal.500"
                        fill="teal.500"
                        stroke="fg.inverse"
                        boxSize="6"
                      />
                    ) : null}
                  </Td>

                  <Td>
                    <Text color="fg.muted">{course?._count?.courseItems || 0}</Text>
                  </Td>

                  <Td>
                    <Flex align="center">
                      <Avatar
                        size="sm"
                        mr="12px"
                        name={userFullName}
                        src={course?.createdBy?.picture_url ?? ''}
                      />
                      <Text color="fg.muted">{userFullName}</Text>
                    </Flex>
                  </Td>

                  <Td>
                    {(() => {
                      const isSuperseded =
                        course.status === CourseStatusSchema.enum.SUPERSEDED ||
                        course.status === CourseStatusSchema.enum.PENDING_SUPERSESSION;
                      const supersessionRecord = course.courseSupersessionHistory?.[0];
                      const supersedingCourse = supersessionRecord?.supersededByCourse;
                      const teachOutEndDate = supersessionRecord?.teachOutEndDate;

                      const badge = (
                        <Badge
                          borderRadius="full"
                          colorScheme={courseStatusColorSchemes[course.status] || 'gray'}
                          variant="solid"
                          textTransform="none"
                        >
                          {startCase(course.status.toLowerCase())}
                        </Badge>
                      );

                      if (!isSuperseded || supersedingCourse == null) {
                        return badge;
                      }

                      const supersedingLabel =
                        supersedingCourse.identifier != null && supersedingCourse.identifier !== ''
                          ? `${supersedingCourse.identifier} ${supersedingCourse.title}`
                          : supersedingCourse.title;

                      const tooltipLabel =
                        teachOutEndDate != null ? (
                          <Trans>
                            Superseded by <strong>{supersedingLabel}</strong>. Enrolments must be
                            complete or transferred by{' '}
                            <strong>{djs(teachOutEndDate).format('DD/MM/YY')}</strong>.
                          </Trans>
                        ) : (
                          <Trans>
                            Superseded by <strong>{supersedingLabel}</strong>.
                          </Trans>
                        );

                      return (
                        <Tooltip label={tooltipLabel} hasArrow placement="top">
                          <Box as="span" display="inline-block">
                            {badge}
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  </Td>

                  <Td>
                    {(() => {
                      const courseCoverage = coverageByCourseId.get(course.id);
                      if (courseCoverage === undefined) {
                        return null;
                      }
                      return (
                        <CourseCoverageBadge
                          resources={courseCoverage.resources}
                          languages={courseCoverage.languages}
                        />
                      );
                    })()}
                  </Td>

                  <Td>
                    <Text color="fg.muted">
                      {djs(course.created_at).format('dddd, DD MMMM YYYY')}
                    </Text>
                  </Td>

                  <Td position="sticky" right={0} zIndex={1} bg="bg.surface" px={8}>
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="Options"
                          icon={<Ellipsis width="20px" height="20px" />}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                          display="inline-flex"
                        />
                        <Portal>
                          <MenuList onClick={(e) => e.stopPropagation()}>
                            <MenuItem
                              icon={<FiEdit2 width={16} height={16} />}
                              aria-label={t`Edit course`}
                              _hover={{ color: 'blue' }}
                              onClick={() => handleEditCourse(course.id)}
                            >
                              <Trans>Edit course</Trans>
                            </MenuItem>

                            <MenuItem
                              icon={<FiCopy width={16} height={16} />}
                              aria-label={t`Duplicate course`}
                              _hover={{ color: 'blue' }}
                              onClick={() => onDuplicateCourseClick(course.id)}
                            >
                              <Trans>Duplicate course</Trans>
                            </MenuItem>
                            <Tooltip
                              label={t`Inactive courses cannot be assigned`}
                              isDisabled={isAssignable}
                              hasArrow
                            >
                              <MenuItem
                                icon={<Landmark width={16} height={16} />}
                                aria-label={t`Assign course`}
                                isDisabled={!isAssignable}
                                isFocusable
                                _hover={isAssignable ? { color: 'blue' } : undefined}
                                onClick={() => {
                                  if (!isAssignable) {
                                    return;
                                  }
                                  setSelectedCourseForAssignment({
                                    id: course.id,
                                    title: course.title,
                                    status: course.status,
                                  });
                                }}
                              >
                                <Trans>Assign course</Trans>
                              </MenuItem>
                            </Tooltip>
                            <MenuItem
                              icon={<Replace width={16} height={16} />}
                              aria-label={
                                course.status === CourseStatusSchema.enum.SUPERSEDED
                                  ? t`View supersession`
                                  : course.status === CourseStatusSchema.enum.PENDING_SUPERSESSION
                                    ? t`Edit supersession`
                                    : t`Mark course as superseded`
                              }
                              _hover={{ color: 'blue' }}
                              onClick={() => onMarkSupersededCourseClick(course.id)}
                            >
                              {course.status === CourseStatusSchema.enum.SUPERSEDED ? (
                                <Trans>View supersession</Trans>
                              ) : course.status === CourseStatusSchema.enum.PENDING_SUPERSESSION ? (
                                <Trans>Edit supersession</Trans>
                              ) : (
                                <Trans>Mark as superseded</Trans>
                              )}
                            </MenuItem>
                            <MenuItem
                              icon={<Trash2 width={16} height={16} />}
                              aria-label={t`Delete course`}
                              color="danger"
                              bg="red.50"
                              onClick={() => {
                                setIsDeleteConfirmationOpen(true);
                                setSelectedCourseId(course.id);
                              }}
                            >
                              <Trans>Delete course</Trans>
                            </MenuItem>
                          </MenuList>
                        </Portal>
                      </Menu>
                    </Box>
                  </Td>
                </Tr>
              );
            })}
        </Tbody>
      </Table>

      {selectedCourseForAssignment ? (
        <CourseAssignmentModal
          isOpen={selectedCourseForAssignment !== null}
          onClose={() => setSelectedCourseForAssignment(null)}
          courseId={selectedCourseForAssignment.id}
          courseTitle={selectedCourseForAssignment.title}
          courseStatus={selectedCourseForAssignment.status}
        />
      ) : null}

      <ConfirmationDialog
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title={<Trans>Delete Course</Trans>}
        body={
          <Box>
            <Trans>You are about to delete this course. Are you sure you want to continue?</Trans>
            {selectedCourseId !== null ? (
              <Box
                mt={4}
                p={4}
                borderColor="danger.subtle"
                bg="bg.danger-subtle"
                borderRadius="8px"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {courses.find((unit) => unit.id === selectedCourseId)?.title}
                </Text>
              </Box>
            ) : null}
          </Box>
        }
        onConfirm={() => {
          if (selectedCourseId != null) {
            handleCourseDelete(selectedCourseId);
          }
          setIsDeleteConfirmationOpen(false);
        }}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </>
  );
}
